/**
 * <s-floating-cart> — Wraps the floating cart bubble (bottom-right, every screen size) and keeps the
 * floating stack (cart bubble + WhatsApp button above it) from covering essential controls.
 *
 * Any element marked [data-floating-avoid] (the product page's Add to cart and Buy it now buttons)
 * that would sit under the stack pushes the stack up just above it while it is there; once the
 * control scrolls away, the stack glides back to its corner. The cart itself is never hidden.
 *
 * State: sets --floating-lift (px) on itself and on [data-whatsapp-fab]; CSS adds it to their
 * `bottom` offsets (config/styles.css › FLOATING CART BUBBLE).
 */
class SFloatingCart extends HTMLElement {
  #fab = null;
  #lift = 0;
  #frame = 0;
  #onChange = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  connectedCallback() {
    this.#fab = document.querySelector('[data-whatsapp-fab]');
    window.addEventListener('scroll', this.#onChange, { passive: true });
    window.addEventListener('resize', this.#onChange, { passive: true });
    this.#onChange();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onChange);
    window.removeEventListener('resize', this.#onChange);
    cancelAnimationFrame(this.#frame);
  }

  #setLift(px) {
    if (px === this.#lift) return;
    this.#lift = px;
    this.style.setProperty('--floating-lift', `${px}px`);
    this.#fab?.style.setProperty('--floating-lift', `${px}px`);
  }

  #update() {
    const targets = [...document.querySelectorAll('[data-floating-avoid]')]
      .map((el) => el.getBoundingClientRect())
      .filter((r) => r.width > 0 && r.height > 0)
      .sort((a, b) => b.top - a.top);
    if (!targets.length) return this.#setLift(0);

    // Resting position of the stack (current geometry minus the lift already applied).
    const cart = this.getBoundingClientRect();
    if (!cart.width) return this.#setLift(0);
    let top = cart.top + this.#lift;
    let left = cart.left;
    let right = cart.right;
    const bottom = cart.bottom + this.#lift;
    const fab = this.#fab;
    if (fab && !fab.hasAttribute('data-hidden')) {
      const f = fab.getBoundingClientRect();
      if (f.width) {
        top = Math.min(top, f.top + this.#lift);
        left = Math.min(left, f.left);
        right = Math.max(right, f.right);
      }
    }

    const gap = 12;
    const height = bottom - top;
    let lift = 0;
    // Bottom-most control first; lifting above one can land on the next, so keep climbing.
    for (const r of targets) {
      const stackTop = top - lift;
      const stackBottom = bottom - lift;
      const overlaps = r.right > left - gap && r.left < right + gap && r.bottom > stackTop - gap && r.top < stackBottom + gap;
      if (overlaps) lift = Math.max(lift, bottom - r.top + gap);
    }
    // Never climb into the header area; stay at least 7rem below the top of the screen.
    const maxLift = Math.max(0, bottom - height - 112);
    this.#setLift(Math.round(Math.min(lift, maxLift)));
  }
}

if (!customElements.get('s-floating-cart')) customElements.define('s-floating-cart', SFloatingCart);
